/**
 * EvvCheckInCard — caregiver shift report banner that handles GPS
 * check-in / check-out for Electronic Visit Verification.
 *
 * Flow:
 *   1. On mount, fetches /api/visits/{visit_id}/evv (if a visit_id is in
 *      query) or attempts to resolve the active visit for this client.
 *   2. If state=scheduled → big "Check in" button. Click → asks for
 *      geolocation permission, POSTs to /checkin, transitions to on_site.
 *   3. If state=on_site → shows arrival time + "Check out" button.
 *   4. If state=completed → shows arrival + departure + duration.
 *
 * If the visit_id can't be resolved, the card hides itself.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, CheckCircle2, Loader2, AlertTriangle, LogOut, Clock } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function fmtDuration(min) {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function getGeolocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Your device doesn't support location services."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      (err) => {
        if (err.code === 1) {
          reject(new Error("Location permission denied. Allow location to check in for EVV."));
        } else if (err.code === 3) {
          reject(new Error("Location request timed out. Try again with a clear view of the sky."));
        } else {
          reject(new Error("Couldn't get your location. Please try again."));
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  });
}

export default function EvvCheckInCard({ clientId }) {
  const [searchParams] = useSearchParams();
  const visitId = searchParams.get("visit_id") || searchParams.get("visit") || null;
  const [evv, setEvv] = useState(null);
  const [loading, setLoading] = useState(!!visitId);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!visitId) { setLoading(false); return; }
    let alive = true;
    api.get(`/visits/${visitId}/evv`)
      .then((res) => { if (alive) setEvv(res.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [visitId]);

  const checkIn = async () => {
    setPending(true);
    try {
      const stamp = await getGeolocation();
      const res = await api.post(`/visits/${visitId}/checkin`, stamp);
      setEvv(res.data);
      if (res.data.geofence_warning) {
        toast.warning("Checked in — but your GPS is outside the client's address zone. Admin will review.");
      } else {
        toast.success("Checked in. Have a great shift!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not check in");
    } finally {
      setPending(false);
    }
  };

  const checkOut = async () => {
    setPending(true);
    try {
      const stamp = await getGeolocation();
      const res = await api.post(`/visits/${visitId}/checkout`, stamp);
      setEvv(res.data);
      toast.success("Checked out. Thanks for your work today.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not check out");
    } finally {
      setPending(false);
    }
  };

  if (!visitId || loading) return null;
  if (!evv) return null;

  const state = evv.state;
  const accent = state === "completed" ? "emerald"
                 : state === "on_site" ? "sky"
                 : "clinical-primary";

  return (
    <div
      className={`rounded-2xl border bg-white p-4 sm:p-5 shadow-sm ${
        state === "completed" ? "border-emerald-300/60"
        : state === "on_site" ? "border-sky-300/60"
        : "border-clinical-border"
      }`}
      data-testid="evv-checkin-card"
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
          state === "completed" ? "bg-emerald-100 text-emerald-700"
          : state === "on_site" ? "bg-sky-100 text-sky-700"
          : "bg-clinical-primary/10 text-clinical-primary"
        }`}>
          <MapPin className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-semibold text-clinical-text">
            Electronic Visit Verification
          </div>
          <div className="mt-0.5 text-xs text-clinical-subtext">
            {state === "scheduled" && "Check in with GPS to start your shift."}
            {state === "on_site" && `Checked in at ${fmtTime(evv.checkin_at)} — currently on site.`}
            {state === "completed" && `Visit complete · ${fmtDuration(evv.duration_min)}`}
          </div>

          {evv.geofence_warning && state !== "scheduled" && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-900 border border-amber-200">
              <AlertTriangle className="h-3 w-3" />
              GPS was outside the client's address zone — admin will review.
            </div>
          )}

          {/* Stamps */}
          {state !== "scheduled" && (
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <Stamp label="Check-in" time={evv.checkin_at} distance={evv.checkin_distance_m} />
              <Stamp label="Check-out" time={evv.checkout_at} distance={evv.checkout_distance_m} />
            </div>
          )}

          {/* CTA */}
          <div className="mt-3.5">
            {state === "scheduled" && (
              <button
                type="button"
                onClick={checkIn}
                disabled={pending}
                className="btn-primary disabled:opacity-50"
                data-testid="evv-checkin-button"
              >
                {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Check in with GPS
              </button>
            )}
            {state === "on_site" && (
              <button
                type="button"
                onClick={checkOut}
                disabled={pending}
                className="btn-secondary disabled:opacity-50"
                data-testid="evv-checkout-button"
              >
                {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                Check out & end visit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stamp({ label, time, distance }) {
  return (
    <div className="rounded-lg border border-clinical-border/60 bg-clinical-bg/40 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-clinical-subtext">
        <Clock className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 text-sm font-medium text-clinical-text">{fmtTime(time)}</div>
      {distance != null && (
        <div className="text-[10px] text-clinical-subtext mt-0.5">
          {Math.round(distance)}m from client
        </div>
      )}
    </div>
  );
}
